import { createAsyncThunk } from '@reduxjs/toolkit'
import {
  fetchCruiseShips,
  createCruiseShip,
  updateCruiseShip,
  deleteCruiseShip,
  uploadCruiseShipImage,
  deleteCruiseShipImage,
} from '../../services/cruiseShips'
import { toast } from 'react-toastify'

export const getCruiseShips = createAsyncThunk(
  'cruiseShips/fetchCruiseShips',
  async (_, thunkAPI) => {
    try {
      const res = await fetchCruiseShips()
      if (res.error) throw res.error
      return res.data
    } catch (err) {
      return thunkAPI.rejectWithValue({ error: err.message })
    }
  },
)

const isFileOrBlob = (obj) => {
  if (!obj) return false
  return (
    obj instanceof File ||
    obj instanceof Blob ||
    (typeof obj === 'object' && typeof obj.size === 'number')
  )
}

/**
 * Process image uploads for a cruise ship.
 * - mainImageFile: File | Blob | null → uploads to slot 'main'
 * - galleryFiles: Array<File | Blob | string | null> → index-based slots (up to 5)
 * - previousGalleryUrls: string[] → old URLs to know which slots were removed
 */
async function processShipImages(shipId, { mainImageFile, galleryFiles, previousGalleryUrls = [] }) {
  const updates = {}

  // Main image
  if (isFileOrBlob(mainImageFile)) {
    const url = await uploadCruiseShipImage(shipId, mainImageFile, 'main')
    if (url) updates.main_image_url = url
  }

  // Gallery images (up to 5 additional slots)
  if (Array.isArray(galleryFiles)) {
    const finalUrls = []

    for (let i = 0; i < 5; i++) {
      const item = galleryFiles[i]

      if (isFileOrBlob(item)) {
        const url = await uploadCruiseShipImage(shipId, item, `gallery_${i}`)
        if (url) finalUrls.push(url)
      } else if (typeof item === 'string' && item.length > 0 && !item.startsWith('blob:')) {
        finalUrls.push(item)
      } else {
        if (previousGalleryUrls[i]) {
          await deleteCruiseShipImage(shipId, `gallery_${i}`)
        }
      }
    }

    updates.gallery_urls = finalUrls
  }

  return updates
}

export const addCruiseShip = createAsyncThunk(
  'cruiseShips/addCruiseShip',
  async (payload, thunkAPI) => {
    try {
      // Support both { shipData, mainImageFile, galleryFiles } and raw shipData
      const shipData = payload.shipData || payload
      const mainImageFile = payload.mainImageFile || null
      const galleryFiles = payload.galleryFiles || null

      const res = await toast.promise(
        (async () => {
          const insertRes = await createCruiseShip(shipData)
          if (insertRes.error) throw insertRes.error
          const created = insertRes.data[0]

          if (isFileOrBlob(mainImageFile) || (galleryFiles && galleryFiles.some(f => isFileOrBlob(f)))) {
            const imageUpdates = await processShipImages(created.id, {
              mainImageFile,
              galleryFiles,
              previousGalleryUrls: [],
            })

            if (Object.keys(imageUpdates).length > 0) {
              const updateRes = await updateCruiseShip(created.id, imageUpdates)
              if (updateRes.error) throw updateRes.error
              return updateRes
            }
          }

          return insertRes
        })(),
        {
          pending: "Registrando barco de crucero...",
          success: "Se registró el barco de crucero 😏",
          error: {
            render({ data: errData }) {
              return errData?.message ? `Error: ${errData.message}` : `Ocurrió un error`
            }
          }
        }
      )
      return res.data[0]
    } catch (err) {
      return thunkAPI.rejectWithValue({ error: err.message })
    }
  }
)

export const editCruiseShip = createAsyncThunk(
  'cruiseShips/editCruiseShip',
  async (payload, thunkAPI) => {
    try {
      // Support both { id, shipData, mainImageFile, galleryFiles, previousGalleryUrls } and { id, ...shipData }
      const id = payload.id
      const shipData = payload.shipData || (({ id, mainImageFile, galleryFiles, previousGalleryUrls, ...rest }) => rest)(payload)
      const mainImageFile = payload.mainImageFile || null
      const galleryFiles = payload.galleryFiles || null
      const previousGalleryUrls = payload.previousGalleryUrls || []

      const res = await toast.promise(
        (async () => {
          let mergedData = { ...shipData }

          if (isFileOrBlob(mainImageFile) || galleryFiles) {
            const imageUpdates = await processShipImages(id, {
              mainImageFile,
              galleryFiles,
              previousGalleryUrls,
            })
            mergedData = { ...mergedData, ...imageUpdates }
          }

          const updateRes = await updateCruiseShip(id, mergedData)
          if (updateRes.error) throw updateRes.error
          return updateRes
        })(),
        {
          pending: "Actualizando barco de crucero...",
          success: "Se actualizó el barco de crucero 😏",
          error: {
            render({ data: errData }) {
              return errData?.message ? `Error: ${errData.message}` : `Ocurrió un error`
            }
          }
        }
      )
      if (!res.data || res.data.length === 0) {
        return { id, ...shipData }
      }
      return res.data[0]
    } catch (err) {
      return thunkAPI.rejectWithValue({ error: err.message })
    }
  }
)

export const removeCruiseShip = createAsyncThunk(
  'cruiseShips/removeCruiseShip',
  async (id, thunkAPI) => {
    try {
      await toast.promise(
        (async () => {
          const res = await deleteCruiseShip(id)
          if (res.error) throw res.error
          return res
        })(),
        {
          pending: "Eliminando barco de crucero...",
          success: "Se eliminó el barco de crucero 😏",
          error: {
            render({ data: errData }) {
              return errData?.message ? `Error: ${errData.message}` : `Ocurrió un error`
            }
          }
        }
      )
      return id
    } catch (err) {
      return thunkAPI.rejectWithValue({ error: err.message })
    }
  }
)
